import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Skeleton,
} from "@facade/components/ui";
import { useEffect, useState } from "react";

import { Building } from "lucide-react";
import { OrganizationAvatarProps } from "./OrganizationAvatar.types";
import { cn } from "@facade/lib/utils";
import { useAttachmentDownloadUrlQuery } from "@facade/queries";

function OrganizationAvatar({
  name,
  imageAttachmentId,
  avatarImageVariation = "small",
  organizationId,
  className,
  ...props
}: OrganizationAvatarProps) {
  const {
    data: pictureDownloadUrl,
    isLoading: isLoadingPictureUrl,
    isFetching: isFetchingPictureUrl,
    isError: isPictureUrlError,
  } = useAttachmentDownloadUrlQuery(
    imageAttachmentId,
    avatarImageVariation === "small" ? "sm" : "original",
    {
      organizationId,
    },
  );

  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageFailed, setImageFailed] = useState(false);

  useEffect(() => {
    setImageLoaded(false);
    setImageFailed(false);
  }, [imageAttachmentId, pictureDownloadUrl?.url]);

  const accessibleName = name
    ? `${name} organization avatar`
    : "Organization avatar";

  const hasPicture = Boolean(imageAttachmentId);
  const pictureUrl = hasPicture ? pictureDownloadUrl?.url : undefined;
  const hasPictureUrl = Boolean(pictureUrl);

  const isResolvingPicture =
    hasPicture &&
    !isPictureUrlError &&
    (isLoadingPictureUrl || isFetchingPictureUrl || !hasPictureUrl);

  const isLoadingImage = hasPictureUrl && !imageLoaded && !imageFailed;
  const shouldShowSkeleton = isResolvingPicture || isLoadingImage;
  const shouldRenderImage = hasPicture && hasPictureUrl && !imageFailed;

  return (
    <Avatar
      key={imageAttachmentId ?? "no-image"}
      aria-label={accessibleName}
      className={cn("after:border-transparent", className)}
      {...props}
    >
      {shouldRenderImage && (
        <AvatarImage
          key={pictureUrl}
          src={pictureUrl}
          alt={accessibleName}
          className="border-accent-higher border"
          onLoad={() => setImageLoaded(true)}
          onError={() => setImageFailed(true)}
        />
      )}

      <AvatarFallback
        className={cn(
          "overflow-hidden bg-background text-muted-foreground",
          "border border-border",
          shouldShowSkeleton && "bg-transparent border-transparent",
        )}
      >
        {shouldShowSkeleton ? (
          <Skeleton className="size-[calc(100%+2px)] rounded-full" />
        ) : (
          <Building className="size-4" />
        )}
      </AvatarFallback>
    </Avatar>
  );
}

export { OrganizationAvatar };
